import React, { useEffect, useLayoutEffect, useState } from "react";
import PropTypes from "prop-types";
import { Box, ButtonBase } from "@mui/material";
import { colors } from "../../theme/colors";
import { MOBILE_NAV_MQ } from "./lobbySettingsStyles";

const PILL_INSET = 3;
const PILL_TRANSITION = "transform 0.12s cubic-bezier(0.22, 1, 0.36, 1)";

export default function MMSegmentedControl({
    options,
    value,
    onChange,
    disabled = false,
    ariaLabel,
}) {
    const [visualValue, setVisualValue] = useState(value);
    const [motionReady, setMotionReady] = useState(false);

    useEffect(() => {
        setVisualValue(value);
    }, [value]);

    useLayoutEffect(() => {
        setMotionReady(true);
    }, []);

    const selectedIndex = Math.max(
        0,
        options.findIndex((option) => option.value === visualValue),
    );
    const optionCount = Math.max(options.length, 1);

    return (
        <Box
            role="radiogroup"
            aria-label={ariaLabel}
            sx={{
                position: "relative",
                display: "flex",
                alignItems: "stretch",
                width: "100%",
                minHeight: "2.35rem",
                p: `${PILL_INSET}px`,
                boxSizing: "border-box",
                borderRadius: "10px",
                background: "rgba(255,255,255,0.035)",
                border: "1px solid rgba(255,255,255,0.07)",
                boxShadow: "inset 0 1px 0 rgba(255,255,255,0.03)",
                [MOBILE_NAV_MQ]: {
                    minHeight: "2.1rem",
                    borderRadius: "9px",
                },
            }}
        >
            <Box
                aria-hidden
                sx={{
                    position: "absolute",
                    top: `${PILL_INSET}px`,
                    bottom: `${PILL_INSET}px`,
                    left: `${PILL_INSET}px`,
                    width: `calc((100% - ${PILL_INSET * 2}px) / ${optionCount})`,
                    borderRadius: "8px",
                    background: "rgba(232,163,61,0.12)",
                    border: `1px solid ${colors.primary.main}`,
                    transform: `translateX(${selectedIndex * 100}%)`,
                    transition: motionReady ? PILL_TRANSITION : "none",
                    willChange: "transform",
                    pointerEvents: "none",
                    zIndex: 0,
                    [MOBILE_NAV_MQ]: {
                        borderRadius: "7px",
                    },
                }}
            />
            {options.map((option, index) => {
                const selected = index === selectedIndex;
                const showDivider =
                    index < options.length - 1 &&
                    index !== selectedIndex &&
                    index + 1 !== selectedIndex;

                return (
                    <ButtonBase
                        key={`${option.label}-${option.value}`}
                        role="radio"
                        aria-checked={selected}
                        disableRipple
                        disableTouchRipple
                        disabled={disabled}
                        onClick={() => {
                            if (disabled || option.value === visualValue) {
                                return;
                            }
                            setVisualValue(option.value);
                            onChange(option.value);
                        }}
                        sx={{
                            position: "relative",
                            zIndex: 1,
                            flex: 1,
                            minWidth: 0,
                            px: "0.2rem",
                            py: "0.35rem",
                            borderRadius: "8px",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: "0.32rem",
                            minWidth: 0,
                            color: selected
                                ? colors.text.primary
                                : "rgba(255,255,255,0.52)",
                            fontSize: "0.68rem",
                            fontWeight: 600,
                            letterSpacing: "0.01em",
                            lineHeight: 1.15,
                            whiteSpace: "nowrap",
                            background: "transparent",
                            border: "none",
                            boxShadow: "none",
                            transition: "none",
                            "&:hover": {
                                color: colors.text.primary,
                                background: "transparent",
                            },
                            "&::after": showDivider
                                ? {
                                      content: '""',
                                      position: "absolute",
                                      right: 0,
                                      top: "22%",
                                      bottom: "22%",
                                      width: "1px",
                                      background: "rgba(255,255,255,0.1)",
                                      pointerEvents: "none",
                                  }
                                : undefined,
                            [MOBILE_NAV_MQ]: {
                                fontSize: "0.58rem",
                                px: "0.08rem",
                                py: "0.28rem",
                                borderRadius: "7px",
                            },
                        }}
                    >
                        {option.icon ? (
                            <Box
                                component="span"
                                sx={{
                                    display: "inline-flex",
                                    flexShrink: 0,
                                    lineHeight: 0,
                                }}
                            >
                                {option.icon}
                            </Box>
                        ) : null}
                        <Box
                            component="span"
                            sx={{
                                minWidth: 0,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                            }}
                        >
                            {option.label}
                        </Box>
                    </ButtonBase>
                );
            })}
        </Box>
    );
}

MMSegmentedControl.propTypes = {
    options: PropTypes.arrayOf(
        PropTypes.shape({
            value: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
                .isRequired,
            label: PropTypes.string.isRequired,
            icon: PropTypes.node,
        }),
    ).isRequired,
    value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    onChange: PropTypes.func.isRequired,
    disabled: PropTypes.bool,
    ariaLabel: PropTypes.string,
};
